import React, { useState, useEffect } from "react";
import { Avatar, message, Spin } from "antd";
import { UserOutlined } from "@ant-design/icons";
import { Paciente } from "../../../api/paciente.api";
import {
  getDepartamentos,
  getMunicipios,
} from "../../../api/Catalogos/catalogos.api";
import { Departamento } from "../../../api/Catalogos/types/CAT-012.types";
import { MunicipioItem } from "../../../api/Catalogos/types/CAT-013.types";
import { useLocationResolver } from "../../../hooks/useLocationResolver";
import styles from "./card.module.scss";

interface UserInfoCardProps {
  paciente: Paciente;
}

const displayValue = (value: string | number | null | undefined): string => {
  if (value === 0) return "0";
  if (value === null || value === undefined || String(value).trim() === "") {
    return "N/A";
  }
  return String(value);
};

const displayDUI = (dui: string | null | undefined): string => {
  if (dui === null || dui === undefined || String(dui).trim() === "") {
    return "N/A";
  }
  return String(dui);
};

const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return "N/A";
  try {
    const date = new Date(dateString + "T00:00:00");
    if (isNaN(date.getTime())) return "Fecha inválida";
    return new Intl.DateTimeFormat("es-SV", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(date);
  } catch (e) {
    message.error("Error al formatear la fecha: " + e);
    return dateString;
  }
};

const UserInfoCard: React.FC<UserInfoCardProps> = ({ paciente }) => {
  const [departamentos, setDepartamentos] = useState<Departamento[]>([]);
  const [municipios, setMunicipios] = useState<MunicipioItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Usar el hook de resolución de ubicaciones
  const locationResolver = useLocationResolver(departamentos, municipios);

  useEffect(() => {
    const loadCatalogos = async () => {
      try {
        setIsLoading(true);
        const [departamentosData, municipiosData] = await Promise.all([
          getDepartamentos(),
          getMunicipios(),
        ]);
        setDepartamentos(departamentosData);
        setMunicipios(municipiosData);
      } catch (error) {
        console.error("Error al cargar catálogos:", error);
        message.error("Error al cargar información de ubicación");
      } finally {
        setIsLoading(false);
      }
    };

    loadCatalogos();
  }, []);

  // Función para obtener el nombre del departamento por ID
  const getDepartamentoNombre = React.useCallback(
    (departamentoId: string | number): string => {
      if (!departamentoId) return "N/A";

      // Si estamos cargando o no hay catálogos, mostrar el valor tal como viene
      if (isLoading || departamentos.length === 0) {
        return displayValue(departamentoId);
      }

      const nombre = locationResolver.getDepartamentoNombreById(departamentoId);
      if (nombre) {
        return nombre;
      }

      // Si no es un número válido, podría ser un nombre
      const searchValue = String(departamentoId).trim();
      const numericValue = Number(departamentoId);
      if (isNaN(numericValue) && searchValue.length > 2) {
        return searchValue;
      }

      return displayValue(departamentoId);
    },
    [departamentos, isLoading, locationResolver]
  );

  // Función para obtener el nombre del municipio por ID
  const getMunicipioNombre = React.useCallback(
    (municipioId: string | number): string => {
      if (!municipioId) return "N/A";

      // Si estamos cargando o no hay catálogos, mostrar el valor tal como viene
      if (isLoading || municipios.length === 0) {
        return displayValue(municipioId);
      }

      const nombre = locationResolver.getMunicipioNombreById(municipioId);
      if (nombre) {
        return nombre;
      }

      // Si no es un número válido, podría ser un nombre
      const searchValue = String(municipioId).trim();
      const numericValue = Number(municipioId);
      if (isNaN(numericValue) && searchValue.length > 2) {
        return searchValue;
      }

      return displayValue(municipioId);
    },
    [municipios, isLoading, locationResolver]
  );

  // Función para obtener el nombre del distrito por ID
  const getDistritoNombre = React.useCallback(
    (distritoId: string | number, municipioId?: string | number): string => {
      if (!distritoId) return "N/A";

      // Si estamos cargando o no hay catálogos, mostrar el valor tal como viene
      if (
        isLoading ||
        (departamentos.length === 0 && municipios.length === 0)
      ) {
        return displayValue(distritoId);
      }

      const nombre = locationResolver.getDistritoNombreById(
        distritoId,
        municipioId
      );
      if (nombre) {
        return nombre;
      }

      // Si no es un número válido, podría ser un nombre
      const searchValue = String(distritoId).trim();
      const numericValue = Number(distritoId);
      if (isNaN(numericValue) && searchValue.length > 2) {
        return searchValue;
      }

      return displayValue(distritoId);
    },
    [departamentos, municipios, isLoading, locationResolver]
  );

  const nombreCompleto = [
    paciente.nombre1,
    paciente.nombre2,
    paciente.apellido1,
    paciente.apellido2,
  ]
    .filter(
      (val) => val !== null && val !== undefined && String(val).trim() !== ""
    )
    .join(" ");

  const InfoField = ({
    label,
    value,
    className = "",
    isImportant = false,
  }: {
    label: string;
    value: string | number;
    className?: string;
    isImportant?: boolean;
  }) => (
    <div className={`${styles["user-info-card__field"]} ${className}`}>
      <span className={styles["user-info-card__label"]}>{label}</span>
      <span
        className={`${styles["user-info-card__value"]} ${
          value === "N/A" ? styles["user-info-card__value--na"] : ""
        } ${isImportant ? styles["user-info-card__value--important"] : ""}`}
      >
        {value}
      </span>
    </div>
  );

  return (
    <div className={styles["user-info-card"]}>
      <div className={styles["user-info-card__container"]}>
        <div className={styles["user-info-card__avatar"]}>
          <Avatar size={120} shape="circle" icon={<UserOutlined />} />
        </div>

        <div className={styles["user-info-card__content"]}>
          <h3 className={styles["user-info-card__title"]}>
            Información del Paciente
          </h3>

          {isLoading ? (
            <div style={{ textAlign: "center", padding: "20px" }}>
              <Spin size="large" />
              <p style={{ marginTop: "10px" }}>
                Cargando información de ubicación...
              </p>
            </div>
          ) : (
            <div className={styles["user-info-card__grid"]}>
              <InfoField
                label="Código Paciente"
                value={displayValue(paciente.codigoPaciente)}
                isImportant={true}
              />
              <InfoField
                label="Nombre Completo"
                value={nombreCompleto || "N/A"}
                className={styles["user-info-card__field--two-columns"]}
              />
              <InfoField
                label="Fecha Nacimiento"
                value={formatDate(paciente.fechaNac)}
              />
              <InfoField label="Sexo" value={displayValue(paciente.sexo)} />
              <InfoField
                label="Estado Civil"
                value={displayValue(paciente.estadoCivil)}
              />
              <InfoField
                label="Ocupación"
                value={displayValue(paciente.ocupacion)}
              />
              <InfoField label="Email" value={displayValue(paciente.email)} />
              <InfoField label="DUI" value={displayDUI(paciente.dui)} />

              <InfoField
                label="Numero de teléfono"
                value={displayValue(paciente.telefono)}
              />
              <InfoField
                label="Numero de celular"
                value={displayValue(paciente.celular)}
              />
              <InfoField
                label="Dirección"
                value={
                  [
                    paciente.direccion,
                    getDepartamentoNombre(paciente.departamento),
                  ]
                    .filter(Boolean)
                    .join(", ") || "N/A"
                }
                className={styles["user-info-card__field--full-width"]}
              />
              <InfoField
                label="Municipio"
                value={getMunicipioNombre(paciente.municipio)}
              />
              <InfoField
                label="Distrito"
                value={getDistritoNombre(paciente.distrito, paciente.municipio)}
              />
              <InfoField
                label="Lugar de Trabajo"
                value={displayValue(paciente.lugarDeTrabajo)}
              />
              <InfoField
                label="Teléfono Trabajo"
                value={displayValue(paciente.telefonoTrabajo)}
              />
            </div>
          )}

          {/* Sección de contactos */}
          {!isLoading && (
            <div className={styles["contact-section"]}>
              <h4 className={styles["contact-section__title"]}>
                Contactos de Emergencia
              </h4>
              <div className={styles["contact-section__grid"]}>
                <div>
                  <InfoField
                    label="Nombre persona responsable 1"
                    value={displayValue(paciente.perRepon)}
                  />
                  <InfoField
                    label="Nombre persona responsable 2"
                    value={displayValue(paciente.perRepon2)}
                  />
                  <InfoField
                    label="Numero de teléfono"
                    value={displayValue(paciente.telefono2)}
                  />
                  <InfoField
                    label="Numero de celular"
                    value={displayValue(paciente.celular2)}
                  />
                  <InfoField
                    label="Email"
                    value={displayValue(paciente.email2)}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserInfoCard;
